import { Loader2, RadarIcon } from "lucide-react"
import { ReactNode, useEffect, useRef } from "react"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../molecules/Card"
import { cn } from "../../utils"

type Props = {
  title?: string
  description?: string
  headerAction?: ReactNode
  chart: ReactNode
  footer?: ReactNode
  isPending: boolean
  noData: {
    show: boolean | undefined
    cardTitle?: string
    title: string
    description?: string
  }
  fixedWidth?: boolean
  autoHeight?: boolean
  chartContainerClassName?: string
  shouldScrollToEnd?: boolean
}

export const ChartCard = ({
  title,
  description,
  headerAction,
  chart,
  footer,
  isPending,
  noData,
  fixedWidth = false,
  autoHeight = false,
  chartContainerClassName,
  shouldScrollToEnd = true,
}: Props) => {
  const chartRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onResize = () => {
      if (chartRef.current && chart && shouldScrollToEnd) {
        chartRef.current.scrollLeft = chartRef.current.scrollWidth
      }
    }

    onResize()

    chartRef.current?.addEventListener("resize", onResize)

    return () => {
      chartRef.current?.removeEventListener("resize", onResize)
    }
  }, [chart, shouldScrollToEnd])

  const header = title && (
    <CardHeader className="p-0 items-start gap-2">
      <div className="flex w-full items-center justify-between gap-4">
        <CardTitle>{title}</CardTitle>
        {headerAction}
      </div>
      {description && <CardDescription className="text-sm">{description}</CardDescription>}
    </CardHeader>
  )

  if (isPending) {
    return (
      <Card
        className={cn(
          "flex flex-col min-w-xs p-6 gap-4",
          autoHeight ? "h-auto" : "h-full h-[400px]",
          !fixedWidth && "w-full",
        )}
      >
        {header}
        <CardContent
          className={cn(
            "flex flex-row items-center justify-center p-0",
            autoHeight ? "min-h-24" : "h-full min-h-[320px] flex-1",
          )}
        >
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    )
  }

  const noDataContent = (
    <CardContent
      className={cn(
        "flex flex-col z-10 p-0 text-center items-center justify-center gap-2 max-w-sm",
        autoHeight
          ? "relative py-10 m-auto"
          : "h-full absolute inset-0 gap-6 py-14 m-auto",
      )}
    >
      <RadarIcon className="size-10 text-muted-foreground mb-3" strokeWidth={1.5} />
      <h2 className="text-md text-foreground text-stroke">{noData.title}</h2>
      <p className="text-sm text-muted-foreground text-stroke-sm">{noData.description}</p>
    </CardContent>
  )

  return (
    <Card
      className={cn(
        "flex flex-col min-w-xs p-6 gap-4",
        autoHeight ? "h-auto" : "h-full h-[400px]",
        !fixedWidth && "w-full",
      )}
    >
      {header}
      {noData.show && noDataContent}
      {(!noData.show || !autoHeight) && (
        <CardContent
          ref={chartRef}
          className={cn(
            "p-0 overflow-x-auto",
            autoHeight ? "flex-none" : "flex-1 min-h-[320px]",
            noData.show && "pointer-events-none grayscale opacity-12 dark:opacity-8",
            chartContainerClassName,
          )}
        >
          {chart}
        </CardContent>
      )}
      {footer && (
        <CardFooter className=" flex flex-col items-center gap-2 text-sm p-0">{footer}</CardFooter>
      )}
    </Card>
  )
}
